import { Fragment, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  useListSpaces,
  useUpdateSpace,
  getListSpacesQueryKey,
} from "@workspace/api-client-react";
import type { Space } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { SpacePlansInline } from "@/components/spaces/space-plans-inline";
import { ChevronDown, ChevronRight } from "lucide-react";

const EditSpaceSchema = z.object({
  gstin: z.string().nullable().optional(),
  registeredAddress: z.string().nullable().optional(),
  stateCode: z.string().nullable().optional(),
});

type EditSpaceValues = z.infer<typeof EditSpaceSchema>;

export default function Spaces() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: spaces, isLoading } = useListSpaces();
  const updateMutation = useUpdateSpace();

  const [editSpace, setEditSpace] = useState<Space | null>(null);
  // Phase 25 Plan 25-05: per-row inline plans expansion (collapsed by default,
  // not persisted across navigations). The legacy popup editor was retired
  // in Plan 25-06 — chevron expansion is the only plan-mapping affordance.
  const [expandedSpaces, setExpandedSpaces] = useState<Record<number, boolean>>({});

  function toggleExpand(spaceId: number) {
    setExpandedSpaces((prev) => ({ ...prev, [spaceId]: !prev[spaceId] }));
  }

  const form = useForm<EditSpaceValues>({
    resolver: zodResolver(EditSpaceSchema),
    defaultValues: {
      gstin: "",
      registeredAddress: "",
      stateCode: "",
    },
  });

  function openEdit(space: Space) {
    const ext = space as unknown as { gstin?: string | null; registeredAddress?: string | null; stateCode?: string | null };
    setEditSpace(space);
    form.reset({
      gstin: ext.gstin ?? "",
      registeredAddress: ext.registeredAddress ?? "",
      stateCode: ext.stateCode ?? "",
    });
  }

  function handleClose() {
    setEditSpace(null);
    form.reset();
  }

  async function onSubmit(values: EditSpaceValues) {
    if (!editSpace) return;
    try {
      await updateMutation.mutateAsync({
        id: editSpace.id,
        data: {
          gstin: values.gstin || null,
          registeredAddress: values.registeredAddress || null,
          stateCode: values.stateCode || null,
        },
      });
      toast({ title: "Space updated." });
      queryClient.invalidateQueries({ queryKey: getListSpacesQueryKey() });
      handleClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">Spaces</h1>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Space Name</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Operating Model</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>State Code</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-5 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-48" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : spaces?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No spaces found.
                  </TableCell>
                </TableRow>
              ) : (
                spaces?.map((space) => {
                  const ext = space as unknown as { gstin?: string | null; stateCode?: string | null };
                  const isExpanded = !!expandedSpaces[space.id];
                  return (
                    <Fragment key={space.id}>
                      <TableRow>
                        <TableCell className="w-8">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => toggleExpand(space.id)}
                            aria-label={isExpanded ? "Collapse plans" : "Expand plans"}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">{space.name}</TableCell>
                        <TableCell>{space.city}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="font-mono">
                            {space.operatingModel}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {ext.gstin ?? "—"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {ext.stateCode ?? "—"}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {/* Phase 54 Plan 06 (SPACE-UX-HUB-04): drill into the
                              /spaces/:id center-detail hub. A "View" button
                              (not a row click) so the existing chevron-expand
                              quick-glance behavior is untouched. */}
                          <Link href={`/spaces/${space.id}`}>
                            <Button size="sm" variant="outline">View</Button>
                          </Link>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(space)}>Edit</Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow>
                          <TableCell colSpan={7} className="p-0">
                            <SpacePlansInline spaceId={space.id} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={editSpace !== null} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Space — {editSpace?.name}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="gstin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>GSTIN</FormLabel>
                    <FormControl>
                      <Input placeholder="07AABCU9603R1ZX" maxLength={15} {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormDescription>D-25 — appears on invoice header</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="registeredAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Registered Address</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Multi-line registered address" rows={3} {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="stateCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State Code</FormLabel>
                    <FormControl>
                      <Input placeholder="07" maxLength={2} {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormDescription>2-digit state code (e.g. 07 for Delhi). Drives GST split (CGST+SGST vs IGST).</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>Discard changes</Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "Saving…" : "Save Space"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
